import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChannelContext, ConfigContext, OrganisationContext, UserContext } from '../../context';
import { EventOrderBy, OrganisationTagType, UserRole } from '../../services';
import {
  Button,
  ButtonType,
  cookieOptions,
  EVENTS_FEED_ADMIN_COOKIE_KEY,
  EVENTS_FEED_COOKIE_KEY,
  getCssVar,
  GroupItem,
  IconLabel,
  InputField,
  isAdminLayout,
  layoutPath,
  ModalSlidePanel,
  OrderDirection,
  Select,
  Tabs,
  useAppSelector,
  useDebounce,
  useEventsFeed,
} from '../../shared';
import { EventItem } from '../../shared/components/EventItem';
import { NoContent } from '../Error';
import { CreateEventModal } from './CreateEventModal';
import { EventsFeedFilterBadges } from './EventsFeedFilterBadges';
import { EventsFeedRightSide } from './EventsFeedRightSide';
import { PasteEventButton } from './PasteEventButton';

import classes from './EventsFeed.module.scss';

export const EventsFeed: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'events' });

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies, setCookie] = useCookies([cookieKey]);

  const { orderBy = EventOrderBy.START_AT, orderDirection = OrderDirection.ASC } = {
    ...cookies[cookieKey],
  };

  const { eventsFeed, hasNextPage, isFetched } = useAppSelector(({ eventsFeed }) => eventsFeed);

  const { tabLocaliseKey: eventsTabLocaliseKey = '', eventAllowReaderToCreate = false } = {
    ...useContext(ConfigContext).config.elements.events,
  };

  const { organisation } = useContext(OrganisationContext);

  const { roleId } = useContext(UserContext).userProfile;

  const { eventAllowToCreate } = useContext(ChannelContext).channel.permissions;

  const [createEventModalOpened, setCreateEventModalOpened] = useState(false);

  const [filtersOpened, setFiltersOpened] = useState(false);

  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce(query);

  const tabsWrapperRef = useRef<HTMLDivElement>(null);

  const canCreateEvent = useMemo(() => {
    if (!eventAllowToCreate) {
      return false;
    }
    if (roleId === UserRole.CHANNEL_READER) {
      return eventAllowReaderToCreate;
    }
    return true;
  }, [eventAllowToCreate, eventAllowReaderToCreate, roleId]);

  const eventTags = useMemo(
    () => organisation.tags.filter(({ type }) => type === OrganisationTagType.EVENT),
    [organisation.tags]
  );

  const tagId = searchParams.get('tagId');

  const selectedTabIndex = useMemo(() => {
    if (!tagId) {
      return 0;
    }

    const index = eventTags.findIndex(({ id }) => id === Number(tagId));

    return index === -1 ? 0 : index + 1;
  }, [tagId, eventTags]);

  const organisationTagIds = useMemo(() => {
    const selectedTagId = eventTags[selectedTabIndex - 1]?.id;
    return selectedTagId ? [selectedTagId] : [];
  }, [eventTags, selectedTabIndex]);

  const { getEventsFeedItems, loadMoreEventsFeedItems } = useEventsFeed({
    organisationTagIds,
    query: debouncedQuery,
  });

  useEffect(() => {
    getEventsFeedItems();
  }, [getEventsFeedItems]);

  useEffect(() => {
    const tabsWrapper = tabsWrapperRef.current;
    const selectedTab = tabsWrapper?.querySelector('[aria-pressed="true"]');

    if (!tabsWrapper || !selectedTab) {
      return;
    }

    const { left: wrapperLeft, right: wrapperRight } = tabsWrapper.getBoundingClientRect();
    const { left: tabLeft, right: tabRight } = selectedTab.getBoundingClientRect();

    if (tabLeft >= wrapperLeft && tabRight <= wrapperRight) {
      return;
    }

    selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [selectedTabIndex]);

  const loadMore = useCallback(() => {
    loadMoreEventsFeedItems();
  }, [loadMoreEventsFeedItems]);

  const onTabChange = useCallback(
    (index: number) => {
      if (index === 0) {
        navigate(layoutPath('/events'));
      } else {
        navigate(`${layoutPath('/events')}?tagId=${eventTags[index - 1].id}`);
      }
    },
    [navigate, eventTags]
  );

  const handleSortChange = useCallback(
    (value: string) => {
      const splitAt = value.lastIndexOf('_');

      setCookie(
        cookieKey,
        {
          ...cookies[cookieKey],
          orderBy: value.slice(0, splitAt) as EventOrderBy,
          orderDirection: value.slice(splitAt + 1) as OrderDirection,
        },
        cookieOptions()
      );
    },
    [cookieKey, cookies, setCookie]
  );

  const tabItems = useMemo(
    () => [
      { index: 0, name: t('common.all') },
      ...eventTags.map(({ name }, index) => ({ index: index + 1, name })),
    ],
    [eventTags, t]
  );

  const sortItems = useMemo(
    () => [
      {
        id: `${EventOrderBy.PUBLISHED_AT}_${OrderDirection.DESC}`,
        title: ctxT('order-by-PUBLISHED_AT-DESC'),
      },
      {
        id: `${EventOrderBy.CREATED_AT}_${OrderDirection.DESC}`,
        title: ctxT('order-by-CREATED_AT-DESC'),
      },
      {
        id: `${EventOrderBy.UPCOMING}_${OrderDirection.ASC}`,
        title: ctxT('order-by-UPCOMING'),
      },
      {
        id: `${EventOrderBy.UPCOMING_REGISTRATION_END_AT}_${OrderDirection.ASC}`,
        title: ctxT('order-by-UPCOMING_REGISTRATION_END_AT'),
      },
      {
        id: `${EventOrderBy.UPCOMING_END_AT}_${OrderDirection.ASC}`,
        title: ctxT('order-by-UPCOMING_END_AT'),
      },
    ],
    [ctxT]
  );

  const dataLength = useMemo(() => eventsFeed.length, [eventsFeed.length]);

  const appliedFiltersCount = useMemo(
    () =>
      (cookies[cookieKey]?.filter ?? []).reduce(
        (res: number, { groupItems }: { groupItems: GroupItem[] }) =>
          res + (groupItems ?? []).length,
        0
      ),
    [cookieKey, cookies]
  );

  const loader = useMemo(() => {
    return (
      <div className={classes['loader']}>
        <Skeleton height={'5rem'} />
        <Skeleton height={'5rem'} />
        <Skeleton height={'5rem'} />
      </div>
    );
  }, []);

  const content = useMemo(() => {
    if (!isFetched) {
      return loader;
    }

    if (!dataLength) {
      return <NoContent subtitle={ctxT('no-content-subtitle')} />;
    }

    return (
      <InfiniteScroll
        next={loadMore}
        hasMore={hasNextPage}
        dataLength={dataLength}
        loader={loader}
        className={classes['events-feed__scroll']}
      >
        <div className={classes['events-feed__scroll-items']}>
          {eventsFeed.map((event) => (
            <EventItem key={event.id} event={event} />
          ))}
        </div>
      </InfiniteScroll>
    );
  }, [ctxT, dataLength, eventsFeed, hasNextPage, isFetched, loadMore, loader]);

  return (
    <div className={classes['events-feed']}>
      <div className={classes['events-feed__row']}>
        <h1 className={classes['events-feed__row-title']}>{t(eventsTabLocaliseKey)}</h1>

        {!isAdminLayout() && (
          <IconLabel
            className={classes['events-feed__row-filter']}
            iconId={'filter'}
            iconSize={18}
            label={`${t('common.filter')} (${appliedFiltersCount})`}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={() => setFiltersOpened(true)}
          />
        )}

        <PasteEventButton onPasted={getEventsFeedItems} />

        {canCreateEvent && (
          <Button
            type={ButtonType.primary}
            label={ctxT('add-new')}
            onClick={() => setCreateEventModalOpened(true)}
          />
        )}
      </div>

      <div className={classes['events-feed__row']}>
        <InputField
          value={query}
          placeholder={t('common.search')}
          onChange={({ target: { value } }) => setQuery(value)}
        />

        <Select
          selectedItemId={`${orderBy}_${orderDirection}`}
          items={sortItems}
          onChange={(value) => handleSortChange(value as string)}
        />
      </div>

      <ModalSlidePanel
        isOpen={filtersOpened}
        body={<EventsFeedRightSide />}
        onClose={() => setFiltersOpened(false)}
        ariaLabel={t('common.filter')}
      />

      {Boolean(tabItems.length > 1) && (
        <div ref={tabsWrapperRef}>
          <Tabs
            items={tabItems}
            selectedTabIndex={selectedTabIndex}
            setSelectedTabIndex={onTabChange}
            scrollable
            noBackground
          />
        </div>
      )}

      <EventsFeedFilterBadges />

      {content}

      {createEventModalOpened && (
        <CreateEventModal
          isOpened={createEventModalOpened}
          onClose={() => setCreateEventModalOpened(false)}
        />
      )}
    </div>
  );
};
